import { yupResolver } from "@hookform/resolvers/yup";
import { EnvelopeSimple } from "@phosphor-icons/react";
import { Button, Card, Label, Spinner, TextInput } from "flowbite-react";
import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import brandLogo from "../assets/images/Logo.png";
import PasswordInput from "../components/PasswordInput";
import { useAuth } from "../hooks";
import { ILoginPayload } from "../interfaces";

const SignIn: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleCaptchaChange = (token: string | null) => {
    setIsCaptchaVerified(!!token);
    setCaptchaToken(token);
  };

  // Add Yup Schema
  const schema = yup
    .object({
      email: yup
        .string()
        .email(t("auth.login.invalidEmail"))
        .required(t("auth.login.emailRequired")),
      password: yup
        .string()
        .min(6, t("auth.login.passwordMinLength"))
        .required(t("auth.login.passwordRequired")),
    })
    .required();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ILoginPayload>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ILoginPayload) => {
    if (!isCaptchaVerified || !captchaToken) {
      toast.error(
        t("auth.login.errors.captchaRequired") ||
        "Please verify the reCAPTCHA before submitting."
      );
      return;
    }

    try {
      setIsLoading(true);
      const payload: ILoginPayload = { ...data, recaptchaToken: captchaToken };
      login(payload)
        .then(() => {
          setIsLoading(false);
          toast.success(t("auth.login.success"));
          recaptchaRef.current?.reset();
          setIsCaptchaVerified(false);
          setCaptchaToken(null);
        })
        .catch((err) => {
          setIsLoading(false);
          toast.error(err.message);
          recaptchaRef.current?.reset();
          setIsCaptchaVerified(false);
          setCaptchaToken(null);
        });
    } catch (error) {
      setIsLoading(false);
      console.error("Login error:", error);
      toast.error(t("auth.login.error"));
      recaptchaRef.current?.reset();
      setIsCaptchaVerified(false);
      setCaptchaToken(null);
    }
  };
  return (
    <div className="min-h-screen flex flex-col md:bg-none bg-gradient-to-br from-indigo-200 via-indigo-200 to-white">
      <div className="flex-1 flex">
        <div className="w-full flex flex-col items-center justify-center p-4 md:p-8 relative">
          <Card
            className="w-full max-w-xl min-h-[500px] flex flex-col items-center justify-center backdrop-blur-sm bg-white/80"
            theme={{
              root: {
                children:
                  "flex h-full flex-col justify-between gap-4 p-6 sm:p-12",
              },
            }}
          >
            <a
              className="flex w-full justify-center items-center z-20"
              href="https://www.apprevbooster.com"
              target="_blank"
            >
              <img
                src={brandLogo}
                alt="Logo"
                className="w-1/2 object-contain cursor-pointer"
              // onClick={() => navigate("/")}
              />
            </a>
            <div className="text-center  max-w-1000">
              {/* <h2 className="text-2xl font-bold text-gray-900">
                {t("auth.login.title")}
              </h2> */}
              <p className="text-gray-600 ">{t("auth.login.subtitle")}</p>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="w-full "
              name="login"
              autoComplete="on"
            >
              <div>
                <Label htmlFor="email" value={t("auth.login.email")} />
                <TextInput
                  color={errors.email ? "failure" : "indigo"}
                  icon={EnvelopeSimple}
                  id="email"
                  type="email"
                  autoComplete="username"
                  placeholder={t("auth.login.emailPlaceholder")}
                  {...register("email")}
                />
                <p className="text-red-500 text-xs mt-1 min-h-4">
                  {errors.email?.message || ""}
                </p>
              </div>
              <div>
                <Label htmlFor="password" value={t("auth.login.password")} />
                <PasswordInput
                  id="password"
                  color={errors.password ? "failure" : "indigo"}
                  autoComplete="current-password"
                  placeholder={t("auth.login.passwordPlaceholder")}
                  animationDirection="horizontal"
                  {...register("password")}
                />
                <p className="text-red-500 text-xs mt-1 min-h-4">
                  {errors.password?.message || ""}
                </p>
              </div>

              <div className="flex justify-center mb-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  onExpired={() => {
                    setIsCaptchaVerified(false);
                    setCaptchaToken(null);
                  }}
                />
              </div>

              <Button
                type="submit"
                color="indigo"
                className="w-full mt-2"
                disabled={isLoading || !isCaptchaVerified}
                isProcessing={isLoading}
                processingSpinner={<Spinner color={"purple"} size={"sm"} />}
              >
                {isLoading
                  ? t("auth.login.loggingIn")
                  : t("auth.login.loginButton")}
              </Button>
            </form>

            {/* <div className="mt-4 text-center text-sm text-gray-600">
              <p>
                {t("auth.login.terms")}
                <a href="#" className="text-primary-600 hover:underline">
                  {t("auth.login.termsOfService")}
                </a>
                {t("auth.login.and")}
                <a href="#" className="text-primary-600 hover:underline">
                  {t("auth.login.privacyPolicy")}
                </a>
              </p>
            </div> */}
            {/* <div className="text-center text-sm text-gray-600">
              <p>
                {t("auth.login.createNewAccountTitle")}
                <span
                  onClick={handleSwap}
                  className="text-primary-600 hover:underline cursor-pointer"
                >
                  {t("auth.login.createNewAccountSubtitle")}
                </span>
              </p>
            </div> */}
            <div className="text-center text-sm text-gray-600">
              <p>
                {t("auth.login.forgotPasswordTitle")} &nbsp;
                <span
                  onClick={() => navigate("/forgot-password")}
                  className="text-primary-600 hover:underline cursor-pointer"
                >
                  {t("auth.login.forgotPasswordSubtitle")}
                </span>
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
